import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  AuthenticationResult, 
  AccountInfo,
  InteractionRequiredAuthError,
  SilentRequest
} from '@azure/msal-browser';
import * as microsoftTeams from "@microsoft/teams-js";
import { msalInstance, loginRequest, sharePointTokenRequest, sharePointSilentRequest } from '../config/msalConfig';
import { invoiceService } from '../services/InvoiceService';
import { sharepointService } from '../services/SharePointService';

const SHAREPOINT_SITE_URL = process.env.REACT_APP_SHAREPOINT_SITE_URL || 'https://igniagroup.sharepoint.com/sites/projects/';

interface AuthContextType {
  user: AccountInfo | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
  error: string | null;
  isInTeams: boolean;
  hideLoginButton: boolean;
}


const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AccountInfo | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isInTeams, setIsInTeams] = useState<boolean>(false);
  const [hideLoginButton, setHideLoginButton] = useState<boolean>(false);

  const getAccessTokenInternal = async (account: AccountInfo): Promise<string | null> => {
    try {
      // First try to get SharePoint-specific token silently
      const request: SilentRequest = {
        ...sharePointSilentRequest,
        account: account,
      };

      console.log('Requesting SharePoint token with scopes:', request.scopes);
      const response = await msalInstance.acquireTokenSilent(request);
      console.log('SharePoint token acquired successfully');
      return response.accessToken;
    } catch (err) {
      console.log('Silent token acquisition failed, trying popup:', err);
      if (err instanceof InteractionRequiredAuthError) {
        try {
          const response = await msalInstance.acquireTokenPopup(sharePointTokenRequest);
          console.log('SharePoint token acquired via popup');
          return response.accessToken;
        } catch (popupErr: any) {
          console.error('SharePoint token acquisition failed:', popupErr);
          setError(`Token acquisition failed: ${popupErr.message}`);
          return null;
        }
      } else {
        console.error('Silent SharePoint token acquisition failed:', err);
        return null;
      }
    }
  };

  useEffect(() => {
    const initializeMsal = async () => {
      try {
        // Check if running in Teams context (non-blocking with short timeout)
        const checkTeamsContext = async () => {
          try {
            // Set a very short timeout for Teams context check
            const teamsContextPromise = microsoftTeams.app.getContext();
            const timeoutPromise = new Promise((_, reject) => 
              setTimeout(() => reject(new Error('Teams timeout')), 500)
            );
            
            await Promise.race([teamsContextPromise, timeoutPromise]);
            setIsInTeams(true);
            console.log('[Auth] Running in Teams context');
            return true;
          } catch (err) {
            setIsInTeams(false);
            console.log('[Auth] Running in browser, not Teams');
            return false;
          }
        };

        // Start Teams check immediately but don't wait for invoice service
        const teamsCheckPromise = checkTeamsContext();
        
        // Initialize invoice service and MSAL in parallel
        await Promise.all([
          invoiceService.initialize(),
          msalInstance.initialize()
        ]);

        const redirectResponse = await msalInstance.handleRedirectPromise();
        
        // Now wait for Teams check result
        const inTeams = await teamsCheckPromise;
        
        // Restore a cached or redirect-authenticated account before starting a new login.
        const accounts = msalInstance.getAllAccounts();
        const account = redirectResponse?.account ?? msalInstance.getActiveAccount() ?? accounts[0];
        if (account) {
          msalInstance.setActiveAccount(account);
          setUser(account);
          setIsAuthenticated(true);

          await sharepointService.initialize(SHAREPOINT_SITE_URL, () => getAccessTokenInternal(account));
          console.log('[Auth] SharePointService initialized (app-only backend + delegated gate check)');
          setIsLoading(false);
          return;
        }

        if (inTeams) {
          // Auto-login in Teams context only
          console.log('[Auth] In Teams but no accounts found, attempting silent login...');
          try {
            setHideLoginButton(true);
            const response: AuthenticationResult = await msalInstance.acquireTokenSilent({
              ...loginRequest,
            });
            
            if (response.account) {
              msalInstance.setActiveAccount(response.account);
              setUser(response.account);
              setIsAuthenticated(true);
              console.log('[Auth] Auto-login successful in Teams');

              await sharepointService.initialize(SHAREPOINT_SITE_URL, () => getAccessTokenInternal(response.account!));
            }
          } catch (silentErr) {
            console.log('[Auth] Silent login failed in Teams, will show login button');
          }
          setHideLoginButton(false);
        }

        if (!msalInstance.getActiveAccount()) {
          // Reuse an existing Microsoft 365 browser session when this app has no cached account.
          try {
            const response = await msalInstance.ssoSilent(loginRequest);
            if (response.account) {
              msalInstance.setActiveAccount(response.account);
              setUser(response.account);
              setIsAuthenticated(true);
              await sharepointService.initialize(SHAREPOINT_SITE_URL, () => getAccessTokenInternal(response.account!));
              console.log('[Auth] Signed in using the existing browser session');
            }
          } catch (silentErr) {
            console.log('[Auth] No reusable browser session found; sign-in is required', silentErr);
          }
        }

        setIsLoading(false);
      } catch (err) {
        console.error('MSAL initialization failed:', err);
        setError('Failed to initialize authentication');
        setIsLoading(false);
      }
    };

    initializeMsal();
  }, []);

  const login = async (): Promise<void> => {
    try {
      setError(null);
      setIsLoading(true);
      
      console.log('Starting login process...');
      console.log('Login request:', loginRequest);
      
      const response: AuthenticationResult = await msalInstance.loginPopup(loginRequest);
      
      console.log('Login successful:', response);
      
      if (response.account) {
        msalInstance.setActiveAccount(response.account);
        setUser(response.account);
        setIsAuthenticated(true);

        await sharepointService.initialize(SHAREPOINT_SITE_URL, () => getAccessTokenInternal(response.account!));
        console.log('[Auth] SharePointService initialized (app-only backend + delegated gate check) after login');
      }
    } catch (err: any) {
      console.error('Login failed:', err);
      console.error('Error details:', {
        name: err.name,
        message: err.message,
        errorCode: err.errorCode,
        errorMessage: err.errorMessage,
        subError: err.subError
      });
      setError(`Login failed: ${err.errorCode || err.name}: ${err.errorMessage || err.message}`);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      setError(null);
      await msalInstance.logoutPopup({
        postLogoutRedirectUri: window.location.origin,
      });
      
      setUser(null);
      setIsAuthenticated(false);
    } catch (err: any) {
      console.error('Logout failed:', err);
      setError(`Logout failed: ${err.message}`);
    }
  };

  const getAccessToken = async (): Promise<string | null> => {
    if (!user) {
      throw new Error('User not authenticated');
    }
    return getAccessTokenInternal(user);
  };

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    getAccessToken,
    error,
    isInTeams,
    hideLoginButton,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
